import { Link, useRouterState } from "@tanstack/react-router";

import { Sparkles } from "lucide-react";

import { cn } from "@/lib/cn";

import {
  highlighted,
  sectionOf,
  useSectionLabels,
  useSections,
  useSubNav,
} from "./sections";

/**
 * The top bar's sections, like Angel One: a click opens the section's first
 * page, and the section's pages show as tabs under the bar (SubNav). Phones
 * use the drawer instead.
 */
export function TopNav() {
  const sections = useSections();
  const labels = useSectionLabels();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = sectionOf(sections, path);
  return (
    <nav
      aria-label="Main"
      className="hidden h-full items-stretch gap-0.5 lg:flex"
    >
      {[...sections.entries()].map(([group, entries]) => {
        const first = entries[0];
        if (!first) return null;
        const on = group === current;
        return (
          <Link
            key={group}
            to={first.path}
            aria-current={on ? "true" : undefined}
            className={cn(
              "relative inline-flex items-center gap-1.5 px-3 text-sm font-medium transition-colors",
              on ? "text-brand" : "text-muted hover:text-fg",
            )}
          >
            {highlighted(entries) ? (
              <Sparkles
                className="size-3.5 text-[#6d4fd8] dark:text-[#b39dff]"
                aria-hidden
              />
            ) : null}
            {labels[group]}
            {on ? (
              <span
                className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand"
                aria-hidden
              />
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

/** The current section's pages as a row of tabs under the top bar. */
export function SubNav() {
  const { current, entries, path, show } = useSubNav();
  const labels = useSectionLabels();
  if (!show || !current) return null;
  const active = entries
    .filter((e) => path === e.path || path.startsWith(`${e.path}/`))
    .reduce<string | undefined>(
      (best, e) => (best && best.length >= e.path.length ? best : e.path),
      undefined,
    );
  return (
    <nav
      aria-label={`${labels[current]} pages`}
      className="border-b border-border bg-surface"
    >
      <ul className="mx-auto flex max-w-[1600px] gap-1 overflow-x-auto px-2 sm:px-4">
        {entries.map((e) => {
          // the longest matching tab is the current one (/app/feed/following is not My Feed)
          const on = e.path === active;
          const Count = e.nav.badge;
          return (
            <li key={e.path} className="shrink-0">
              <Link
                to={e.path}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "relative inline-flex h-11 items-center px-3 text-sm whitespace-nowrap transition-colors",
                  on ? "font-medium text-brand" : "text-muted hover:text-fg",
                )}
              >
                {e.nav.label}
                {Count ? <Count /> : null}
                {on ? (
                  <span
                    className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand"
                    aria-hidden
                  />
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
